package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.EmptyState
import com.sddamico.components.widgets.ErrorCard
import com.sddamico.components.widgets.PageLoading
import com.sddamico.models.LeagueYearDetailResponse
import com.sddamico.models.TeamStandingResponse
import com.sddamico.util.formatLastUpdated
import com.sddamico.util.formatPoints
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FlipReorder
import com.sddamico.components.motion.SlidingUnderline
import com.sddamico.components.motion.flipId
import com.sddamico.components.motion.stableBoldLabel
import com.sddamico.components.motion.underlineTarget
import org.w3c.dom.HTMLElement
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

private enum class StandingsTab(val label: String) {
    OVERALL("Overall"),
    GOLF("Golf"),
    MENS_TENNIS("Men's Tennis"),
    WOMENS_TENNIS("Women's Tennis"),
}

@InitRoute
fun initStandings(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Standings"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun StandingsPage() {
    var leagueData by remember { mutableStateOf<LeagueYearDetailResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var activeTab by remember { mutableStateOf(StandingsTab.OVERALL) }

    LaunchedEffect(Unit) {
        try {
            leagueData = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load standings"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!)
        return
    }

    val data = leagueData
    if (data == null) {
        EmptyState("Season hasn't started yet")
        return
    }

    Column(Modifier.fillMaxWidth().gap(1.5.cssRem)) {
        // Header
        Column {
            SpanText(
                "League Standings",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(2.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )
            if (data.lastSyncedAt != null) {
                SpanText(
                    "Updated ${formatLastUpdated(data.lastSyncedAt!!)}",
                    Modifier.fontSize(0.8125.cssRem).color(SitePalette.textMuted).margin(top = 0.25.cssRem)
                )
            }
        }

        // Card container
        Column(
            Modifier
                .fillMaxWidth()
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(8.px)
                .padding(12.px)
                .overflow(Overflow.Hidden)
        ) {
            // Tab bar
            SlidingUnderline(
                activeKey = activeTab,
                color = SitePalette.primary,
                gap = (-2).px,
                modifier = Modifier.fillMaxWidth().overflow(Overflow.Auto),
            ) {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .borderBottom(1.px, LineStyle.Solid, SitePalette.border)
                        .gap(0.px)
                ) {
                    StandingsTab.entries.forEach { tab ->
                        val isActive = tab == activeTab
                        Div(
                            Modifier
                                .padding(topBottom = 0.75.cssRem, leftRight = 1.25.cssRem)
                                .color(if (isActive) SitePalette.primary else SitePalette.textMuted)
                                .fontSize(0.875.cssRem)
                                .fontWeight(if (isActive) FontWeight.SemiBold else FontWeight.Medium)
                                .cursor(Cursor.Pointer)
                                .transition(Transition.of("color", Durations.HOVER.ms, Ease.Out.fn))
                                .onClick { activeTab = tab }
                                .styleModifier { property("white-space", "nowrap") }
                                .underlineTarget(isActive)
                                .toAttrs()
                        ) {
                            SpanText(tab.label, Modifier.stableBoldLabel(tab.label))
                        }
                    }
                }
            }

            // Table
            Div(
                Modifier
                    .fillMaxWidth()
                    .overflow(Overflow.Auto)
                    .toAttrs()
            ) {
                StandingsTable(activeTab, data.standings)
            }
        }
    }
}

private class StandingsRow(val team: TeamStandingResponse, val points: Double, val provisionalPoints: Double)

/**
 * One table for every tab, with rows keyed by club, so switching tabs moves each club's row to its new place
 * instead of rebuilding the table.
 */
@Composable
private fun StandingsTable(activeTab: StandingsTab, standings: List<TeamStandingResponse>) {
    val rows = when (activeTab) {
        StandingsTab.OVERALL -> standings.map { StandingsRow(it, it.overallScore, 0.0) }
        StandingsTab.GOLF -> standings.sortedByDescending { it.golfPoints }
            .map { StandingsRow(it, it.golfPoints, it.provisionalGolfPoints) }
        StandingsTab.MENS_TENNIS -> standings.sortedByDescending { it.mensTennisPoints }
            .map { StandingsRow(it, it.mensTennisPoints, it.provisionalMensTennisPoints) }
        StandingsTab.WOMENS_TENNIS -> standings.sortedByDescending { it.womensTennisPoints }
            .map { StandingsRow(it, it.womensTennisPoints, it.provisionalWomensTennisPoints) }
    }
    val isOverall = activeTab == StandingsTab.OVERALL
    var tbody by remember { mutableStateOf<HTMLElement?>(null) }
    FlipReorder(activeTab, tbody)

    Table(
        Modifier
            .fillMaxWidth()
            .styleModifier { property("border-collapse", "collapse") }
            .toAttrs()
    ) {
        Thead {
            Tr(Modifier.borderBottom(1.px, LineStyle.Solid, SitePalette.border).toAttrs()) {
                HeaderCell("Rank", "3rem")
                HeaderCell("Team", null, grow = true)
                if (isOverall) {
                    HeaderCell("Golf", "4rem")
                    HeaderCell("Men's", "4rem")
                    HeaderCell("Women's", "4rem")
                    HeaderCell("Score", "4rem", accent = true)
                } else {
                    HeaderCell("Points", "5rem", accent = true)
                    HeaderCell("Pending", "5rem")
                }
            }
        }
        Tbody(attrs = {
            ref { element ->
                tbody = element
                onDispose { tbody = null }
            }
        }) {
            rows.forEachIndexed { index, row ->
                val team = row.team
                key(team.clubId) {
                    Tr(
                        Modifier
                            .borderBottom(1.px, LineStyle.Solid, SitePalette.border)
                            // Opaque so rows passing each other mid-reorder occlude instead of overprinting.
                            .backgroundColor(SitePalette.surface)
                            .flipId(team.clubId)
                            .toAttrs()
                    ) {
                        RankCell(index + 1)
                        Td(
                            Modifier
                                .padding(topBottom = 0.75.cssRem, leftRight = 0.5.cssRem)
                                .fontSize(0.875.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .color(SitePalette.text)
                                .toAttrs()
                        ) {
                            Link(
                                "/clubhouse?clubId=${team.clubId}",
                                team.clubName,
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                            )
                        }
                        if (isOverall) {
                            DataCell("#${team.golfRank}")
                            DataCell("#${team.mensTennisRank}")
                            DataCell("#${team.womensTennisRank}")
                            PointsCell(formatPoints(row.points))
                        } else {
                            PointsCell(formatPoints(row.points))
                            PendingPointsCell(row.provisionalPoints)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HeaderCell(text: String, width: String?, grow: Boolean = false, accent: Boolean = false) {
    Th(
        Modifier
            .padding(topBottom = 0.75.cssRem, leftRight = 0.5.cssRem)
            .fontSize(0.6875.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .color(if (accent) SitePalette.accent else SitePalette.textMuted)
            .textAlign(if (grow) TextAlign.Start else TextAlign.End)
            .run { if (width != null) styleModifier { property("width", width) } else this }
            .run { if (grow) flexGrow(1) else this }
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun RankCell(rank: Int) {
    Td(
        Modifier
            .padding(topBottom = 0.75.cssRem, leftRight = 0.5.cssRem)
            .fontSize(0.875.cssRem)
            .fontWeight(FontWeight.Bold)
            .color(
                when (rank) {
                    1 -> SitePalette.accent
                    2 -> SitePalette.textSecondary
                    3 -> SitePalette.accentMuted
                    else -> SitePalette.textMuted
                }
            )
            .textAlign(TextAlign.End)
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs()
    ) {
        Text("$rank")
    }
}

@Composable
private fun DataCell(text: String) {
    Td(
        Modifier
            .padding(topBottom = 0.75.cssRem, leftRight = 0.5.cssRem)
            .fontSize(0.8125.cssRem)
            .color(SitePalette.textSecondary)
            .textAlign(TextAlign.End)
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun PointsCell(text: String) {
    Td(
        Modifier
            .padding(topBottom = 0.75.cssRem, leftRight = 0.5.cssRem)
            .fontSize(0.875.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .color(SitePalette.accent)
            .textAlign(TextAlign.End)
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun PendingPointsCell(provisionalPoints: Double) {
    Td(
        Modifier
            .padding(topBottom = 0.75.cssRem, leftRight = 0.5.cssRem)
            .fontSize(0.8125.cssRem)
            .fontWeight(FontWeight.Medium)
            .fontStyle(FontStyle.Italic)
            .color(SitePalette.textMuted)
            .textAlign(TextAlign.End)
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs()
    ) {
        if (provisionalPoints > 0) {
            Text("+${formatPoints(provisionalPoints)}")
        } else {
            Text("\u2014")
        }
    }
}

